import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Calendar, MessageSquare, Trash2, CheckSquare, Filter } from "lucide-react";
import { format, parseISO, isBefore, startOfToday } from "date-fns";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TaskDialog } from "@/components/tasks/TaskDialog";

const PRIO = { urgent: "bg-red-500/20 text-red-300 border-red-500/40", high: "bg-amber-500/15 text-amber-300 border-amber-500/40", normal: "border-border opacity-70", low: "border-border opacity-50" };

export default function TaskBoard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [area, setArea] = useState("all");
  const [mine, setMine] = useState(false);
  const [dlg, setDlg] = useState(null); // null | "new" | task
  const [drag, setDrag] = useState(null);

  const load = async () => { try { const r = await api.get("/tasks/board"); setData(r.data); } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't load board"); } };
  useEffect(() => { load(); }, []);

  const tasks = useMemo(() => (data?.tasks || []).filter(t => (area === "all" || t.area === area) && (!mine || (t.assignee_ids || []).includes(user?.id))), [data, area, mine, user]);

  const move = async (task, column, position) => {
    if (!(data.can_edit || (task.assignee_ids || []).includes(user?.id))) return;
    setData(d => ({ ...d, tasks: d.tasks.map(t => t.id === task.id ? { ...t, column } : t) }));
    try { await api.post(`/tasks/${task.id}/move`, { column, position }); load(); window.dispatchEvent(new Event("admin-queue-changed")); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); load(); }
  };

  if (!data) return <div className="opacity-50 font-display tracking-widest">LOADING…</div>;
  const overdue = (t) => t.due_date && t.column !== "done" && isBefore(parseISO(t.due_date), startOfToday());

  return (
    <div data-testid="task-board">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Committee</div>
          <h1 className="font-display font-black text-4xl md:text-5xl mt-2">Task Board</h1>
          <p className="opacity-60 mt-2 text-sm">{data.can_view_all ? "Drag cards between columns. Assignees get a push when a task lands on them." : "You're seeing only tasks assigned to you."}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-1" data-testid="task-area-filter">
            {["all", ...data.areas].map(a => <button key={a} type="button" onClick={() => setArea(a)} className={`text-[10px] tracking-widest uppercase px-2.5 py-1.5 rounded-full border ${area === a ? "border-[#83B68B] bg-[#83B68B]/15 text-[#83B68B]" : "border-border hover:border-white/40"}`} data-testid={`task-area-${a}`}>{a === "all" ? "All areas" : a}</button>)}
          </div>
          <button type="button" onClick={() => setMine(m => !m)} className={`text-[10px] tracking-widest uppercase px-2.5 py-1.5 rounded-full border inline-flex items-center gap-1 ${mine ? "border-[#83B68B] bg-[#83B68B]/15 text-[#83B68B]" : "border-border"}`} data-testid="task-filter-mine"><Filter className="w-3 h-3" /> Mine</button>
          {data.can_edit && <button type="button" onClick={() => setDlg("new")} className="btn-volt px-4 py-2 rounded-md text-xs tracking-widest uppercase inline-flex items-center gap-1.5" data-testid="task-new"><Plus className="w-3.5 h-3.5" /> New task</button>}
        </div>
      </div>

      <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${data.columns.length}, minmax(260px, 1fr))` }}>
        {data.columns.map(col => {
          const items = tasks.filter(t => t.column === col.key);
          return (
            <div key={col.key} className={`card-tactical rounded-md p-3 min-h-[300px] ${drag ? "border-dashed border-[#83B68B]/50" : ""}`} data-testid={`task-col-${col.key}`}
              onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); if (drag) move(drag, col.key, items.length); setDrag(null); }}>
              <div className="flex items-center justify-between mb-3"><div className="font-display font-bold tracking-tight">{col.name}</div><span className="text-[10px] tracking-widest uppercase opacity-50" data-testid={`task-col-count-${col.key}`}>{items.length}</span></div>
              <div className="space-y-2">
                {items.map(t => (
                  <div key={t.id} draggable={data.can_edit || (t.assignee_ids || []).includes(user?.id)} onDragStart={() => setDrag(t)} onDragEnd={() => setDrag(null)} onClick={() => setDlg(t)}
                    className={`rounded-md border bg-background/60 p-3 cursor-pointer hover:border-[#83B68B]/60 transition-colors ${overdue(t) ? "border-red-500/50" : "border-border"}`} data-testid={`task-card-${t.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-medium text-sm leading-snug">{t.title}</div>
                      <span className={`text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded border shrink-0 ${PRIO[t.priority]}`}>{t.priority}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11px] opacity-70">
                      <span className="text-[#83B68B]">{t.area}</span>
                      {t.due_date && <span className={`inline-flex items-center gap-1 ${overdue(t) ? "text-red-400" : ""}`}><Calendar className="w-3 h-3" />{format(parseISO(t.due_date), "d MMM")}</span>}
                      {(t.checklist || []).length > 0 && <span className="inline-flex items-center gap-1"><CheckSquare className="w-3 h-3" />{t.checklist.filter(c => c.done).length}/{t.checklist.length}</span>}
                      {(t.comments || []).length > 0 && <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" />{t.comments.length}</span>}
                    </div>
                    {(t.assignee_names || []).length > 0 && <div className="flex flex-wrap gap-1 mt-2">{t.assignee_names.map((n, i) => <span key={`${n}-${i}`} className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#83B68B]/15 text-[#83B68B]">{n}</span>)}</div>}
                  </div>
                ))}
                {items.length === 0 && <div className="text-xs opacity-40 text-center py-6">Nothing here</div>}
              </div>
            </div>
          );
        })}
      </div>

      {dlg && <TaskDialog task={dlg === "new" ? null : dlg} board={data} onClose={() => setDlg(null)} onChanged={() => { setDlg(null); load(); window.dispatchEvent(new Event("admin-queue-changed")); }} />}
    </div>
  );
}
